import embla from 'embla';

const selectors = {
  progressBar: '[data-progress-bar]',
  gallery: '[data-gallery]',
  step: '[data-step]',
  video: 'video',
  videoControl: '[data-video-control]'
}

class ComponentProductInstructions extends HTMLElement {
  #carousel = null;
  #observer = null;
  #currentStep = 0;

  constructor() {
    super();
    
    this.handleScroll = this.handleScroll.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleStepPointerEnter = this.handleStepPointerEnter.bind(this);
    this.handleStepPointerLeave = this.handleStepPointerLeave.bind(this);
    this.handleStepIntersection = this.handleStepIntersection.bind(this);
    this.handleVideoControlClick = this.handleVideoControlClick.bind(this);
  }

  handleSelect() {
    const activeStep = this.#carousel.selectedScrollSnap();
    
    this.steps?.forEach((step, index) => {
      step.dataset.active = index === activeStep;
    });
  }

  handleScroll() {
    const progress = this.#carousel.scrollProgress();
    const items = this.#carousel.slideNodes();
    const itemCount = items.length;

    const minMapped = 1 / itemCount;
    const maxMapped = 1;

    const mappedProgress = minMapped + (maxMapped - minMapped) * progress;

    this.progressBar?.style.setProperty('--progress', mappedProgress);
  }

  isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  handleVideoControlClick(event) {
    const video = event.currentTarget.parentElement.querySelector('video');

    if (!video) return;

    if (video.paused) {
      video.play();
      event.currentTarget.setAttribute('data-state', 'playing');
    } else {
      video.pause();
      event.currentTarget.setAttribute('data-state', 'paused');
    }
  }

  handleStepPointerEnter(event) {
    if (this.isTouchDevice()) return;

    const video = event.currentTarget.querySelector('video');
    const videoControl = event.currentTarget.querySelector('[data-video-control]');

    videoControl?.setAttribute('data-state', 'playing');
    video?.play();
  }

  handleStepPointerLeave(event) {
    if (this.isTouchDevice()) return;

    const video = event.currentTarget.querySelector('video');
    const videoControl = event.currentTarget.querySelector('[data-video-control]');

    setTimeout(() => {
      if (!video) return;

      if (!video.paused) {
        video.pause();
        videoControl?.setAttribute('data-state', 'paused');
      }
      
      video.currentTime = 0;
    }, 250)
  }

  handleStepIntersection(event) {
    if (!this.isTouchDevice()) return;

    const step = event.target;
    const video = step.querySelector('video');
    const videoControl = step.querySelector('[data-video-control]');

    if (!video) return;

    if (event.isIntersecting) {
      video.play();
      videoControl?.setAttribute('data-state', 'playing');
    }

    if (!event.isIntersecting) {
      videoControl?.setAttribute('data-state', 'paused');
      video.pause();

      video.currentTime = 0;
    }
  }

  #setupCarousel() {
    this.#carousel = embla(this.gallery, {
      loop: false,
      active: false,
      align: () => 15,
      breakpoints: {
        '(max-width: 800px)': {
          active: true,
        }
      }
    })
  }

  #setupEventListeners() {
    this.#carousel?.on('select', this.handleSelect);
    this.#carousel?.on('scroll', this.handleScroll);
    this.videoControls?.forEach((videoControl) => {
      videoControl.addEventListener('click', this.handleVideoControlClick);
    });
    this.steps?.forEach((step) => {
      step.addEventListener('pointerenter', this.handleStepPointerEnter);
      step.addEventListener('pointerleave', this.handleStepPointerLeave);
    });
  }

  #setupObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.handleStepIntersection(entry);
      });
    }, {
      rootMargin: '0px 10px',
      threshold: 0.4
    });

    this.steps?.forEach((step) => {
      this.#observer.observe(step);
    });
  }

  connectedCallback() {
    this.container = this;
    this.gallery = this.querySelector(selectors.gallery);
    this.progressBar = this.querySelector(selectors.progressBar);
    this.steps = Array.from(this.querySelectorAll(selectors.step));
    this.videoControls = Array.from(this.querySelectorAll(selectors.videoControl));

    this.#setupCarousel();
    this.#setupObserver();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    this.#carousel.off('scroll', this.handleScroll);
    this.#carousel?.off('select', this.handleSelect);
    this.#observer?.disconnect();

    this.videoControls?.forEach((videoControl) => {
      videoControl.removeEventListener('click', this.handleVideoControlClick);
    });

    this.steps?.forEach((step) => {
      step.removeEventListener('pointerenter', this.handleStepPointerEnter);
      step.removeEventListener('pointerleave', this.handleStepPointerLeave);
    });

    this.#carousel.destroy();
  }
}

if (!customElements.get('product-instructions')) {
  customElements.define('product-instructions', ComponentProductInstructions);
}